import ExcelJS from "exceljs";

type ReportRow = {
  Id: number;
  FIRSTNAME?: string;
  LASTNAME?: string;
  DIST_NAM?: string;
  DIST_NUM?: string | number;
  SITE_NAM?: string;
  SITE_NUM?: string | number;
  PERMIT?: string;
  Position?: string;
  SitePos?: string;
  Max_Hours?: string | number;
  Schedule?: string;
  COMMENT?: string;

  TimeSheetDate?: string | Date | null;
  SiteTimeIn?: any;
  SiteTimeOut?: any;
  AdditionalStart?: any;
  AdditionalStop?: any;
  TimeIn?: any;
  TimeOut?: any;
  LunchIn?: any;
  LunchOut?: any;

  [key: string]: any;
};

const thinBorder = {
  top: { style: "thin" as const },
  left: { style: "thin" as const },
  bottom: { style: "thin" as const },
  right: { style: "thin" as const },
};

const dayShort = (d: Date) =>
  ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];

const dowIndex: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4 };

const fmtDate = (d: Date) => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const toMinutes = (t: any): number | null => {
  if (!t) return null;
  if (t instanceof Date) return t.getHours() * 60 + t.getMinutes();
  const s = String(t);
  const parts = s.split(":");
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// 12-hour to match this same report's PDF renderer (ScopeEmployeeTimeSheetPdf's
// formatTime12Hour), which the Excel sheet previously disagreed with.
// Only for display: calcHours is given the raw values, not these strings.
const fmtTime12Hour = (t: any) => {
  if (!t) return "";

  let hours: number;
  let minutes: number;

  if (t instanceof Date) {
    hours = t.getHours();
    minutes = t.getMinutes();
  } else {
    const parts = String(t).split(":");
    if (parts.length < 2) return String(t);
    hours = Number(parts[0]);
    minutes = Number(parts[1]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return String(t);
  }

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12; // Convert 0 to 12 for 12 AM

  return `${displayHours}:${String(minutes).padStart(2, "0")} ${period}`;
};

const calcHours = (timeIn: any, timeOut: any, lunchIn?: any, lunchOut?: any) => {
  const inMin = toMinutes(timeIn);
  const outMin = toMinutes(timeOut);
  if (inMin == null || outMin == null) return 0;

  let mins = outMin - inMin;
  if (mins < 0) mins += 24 * 60;

  const lIn = toMinutes(lunchIn);
  const lOut = toMinutes(lunchOut);
  if (lIn != null && lOut != null) {
    let lunch = lOut - lIn;
    if (lunch < 0) lunch += 24 * 60;
    mins -= lunch;
  }

  return Math.max(0, mins / 60);
};

// Monday week start (local)
const weekStartMonday = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // Sun=0
  const diff = (day + 6) % 7; // Mon=0
  d.setDate(d.getDate() - diff);
  return d;
};

const ScopeEmployeeTimesheetExcel = async (reportData: ReportRow[]) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Employee Timesheets");

  let currentRow = 1;

  // ✅ GLOBAL weeks (same for all employees)
  const currentWeekMonday = weekStartMonday(new Date());
  const lastWeekMonday = new Date(currentWeekMonday);
  lastWeekMonday.setDate(lastWeekMonday.getDate() - 7);

  const currentWeekKey = currentWeekMonday.getTime();
  const lastWeekKey = lastWeekMonday.getTime();

  // group by schedule Id
  const groupedById = reportData.reduce<Record<number, ReportRow[]>>((acc, row) => {
    (acc[row.Id] ??= []).push(row);
    return acc;
  }, {});

  const groups = Object.values(groupedById);

  groups.forEach((rows) => {
    const employee = rows[0];

    // Title
    worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = "SCOPE Employee - Time Sheet";
    worksheet.getCell(`A${currentRow}`).font = { size: 14, bold: true };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: "center" };
    currentRow++;

    // Personal details
    const timeSheetData: any[][] = [
      ["Name:", `${employee?.FIRSTNAME ?? ""} ${employee?.LASTNAME ?? ""}`, "", "", "", "", "", "", "Position:", employee?.SitePos ?? employee?.Position ?? ""],
      ["District:", employee?.DIST_NAM ?? "", "", "", "", "", "", "", "Budget Code:", employee?.DIST_NUM ?? ""],
      ["Max Hours:", `${employee?.Max_Hours ? employee?.Max_Hours : "0"} a week`, "", "", "", "", "", "", "Program:", employee?.SITE_NAM ?? ""],
      ["Schedule:", employee?.Schedule ?? ""],
      ["Comment:", employee?.COMMENT ?? ""],
      ["IMPORTANT NOTE: : You may be assigned additional hours or a location change which you must accommodate in order to meet legally required staff to student ratio."],
      [],
    ];

    timeSheetData.forEach((row, rowIndex) => {
      const worksheetRow = worksheet.getRow(currentRow);
      row.forEach((cell, cellIndex) => {
        worksheetRow.getCell(cellIndex + 1).value = cell;
        if (rowIndex < 3 && (cellIndex === 0 || cellIndex === 8)) {
          worksheetRow.getCell(cellIndex + 1).font = { bold: true };
        } else if ((rowIndex === 3 || rowIndex === 4) && cellIndex === 0) {
          worksheetRow.getCell(cellIndex + 1).font = { bold: true };
        }
      });
      currentRow++;
    });

    // Hours Worked header
    worksheet.getCell(`C${currentRow}`).value = "Hours Worked \n (Record Actual time worked)";
    worksheet.getCell(`C${currentRow}`).font = { bold: true };
    currentRow++;

    // Template (Mon–Fri twice)
    const hoursWorkedData = [
      ["", "Date", "In", "Out", "Lunch In", "Lunch Out", "#Hours"],
      // Top block = LAST WEEK
      ["Mon", "", "", "", "", "", ""],
      ["Tue", "", "", "", "", "", ""],
      ["Wed", "", "", "", "", "", ""],
      ["Thu", "", "", "", "", "", ""],
      ["Fri", "", "", "", "", "", ""],
      // Bottom block = CURRENT WEEK
      ["Mon", "", "", "", "", "", ""],
      ["Tue", "", "", "", "", "", ""],
      ["Wed", "", "", "", "", "", ""],
      ["Thu", "", "", "", "", "", ""],
      ["Fri", "", "", "", "", "", ""],
    ];

    const tableStartRow = currentRow;

    // Draw table (A-G)
    hoursWorkedData.forEach((row, rowIndex) => {
      const worksheetRow = worksheet.getRow(tableStartRow + rowIndex);
      row.forEach((value, colIndex) => {
        const cell = worksheetRow.getCell(colIndex + 1);
        cell.value = value;
        cell.border = thinBorder;
        if (rowIndex === 0) cell.font = { bold: true };
      });
    });

    // Fill values from timesheet rows (only last week + current week)
    const dated = rows
      .filter((r) => r.TimeSheetDate)
      .map((r) => ({ r, d: new Date(r.TimeSheetDate as any) }))
      .filter(({ d }) => !Number.isNaN(d.getTime()))
      .sort((a, b) => a.d.getTime() - b.d.getTime());

    let totalHours = 0;

    for (const { r, d } of dated) {
      const dow = dayShort(d);
      if (!dowIndex.hasOwnProperty(dow)) continue;

      const wk = weekStartMonday(d).getTime();

      // blockStartIndex within template:
      // last week -> rows 1..5 (start at index 1)
      // current week -> rows 6..10 (start at index 6)
      let blockStartIndex: number | null = null;
      if (wk === lastWeekKey) blockStartIndex = 1;
      else if (wk === currentWeekKey) blockStartIndex = 6;
      else continue; // ignore other weeks

      const targetRow = tableStartRow + blockStartIndex + dowIndex[dow];

      // if already filled (duplicate same day), skip (or you can override)
      const existingDate = worksheet.getRow(targetRow).getCell(2).value;
      if (existingDate) continue;

      const inVal = r.TimeIn ?? r.SiteTimeIn;
      const outVal = r.TimeOut ?? r.SiteTimeOut;

      worksheet.getRow(targetRow).getCell(2).value = fmtDate(d);
      worksheet.getRow(targetRow).getCell(3).value = fmtTime12Hour(inVal);
      worksheet.getRow(targetRow).getCell(4).value = fmtTime12Hour(outVal);
      worksheet.getRow(targetRow).getCell(5).value = fmtTime12Hour(r.LunchIn);
      worksheet.getRow(targetRow).getCell(6).value = fmtTime12Hour(r.LunchOut);

      const rowHours = calcHours(inVal, outVal, r.LunchIn, r.LunchOut);
      const hCell = worksheet.getRow(targetRow).getCell(7);
      hCell.value = Number(rowHours.toFixed(2));
      hCell.numFmt = "0.00";

      totalHours += rowHours;
    }

    currentRow = tableStartRow + hoursWorkedData.length + 1;

    // Totals row
    worksheet.getRow(currentRow).values = ["", "", "", "", "", "Total:", ""];
    const totalCell = worksheet.getRow(currentRow).getCell(7);
    totalCell.value = Number(totalHours.toFixed(2));
    totalCell.numFmt = "0.00";
    currentRow += 2;

    // (rest of your content unchanged...)
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = "For documenting time not at work indicate";
    worksheet.getCell(`A${currentRow}`).border = thinBorder;
    worksheet.getRow(currentRow).height = 30;
    currentRow++;

    const timeNotAtWorkRows = [
      { left: "Sick (Personal Illness or Immediate Family)", right: "Jury Duty" },
      { left: "Personal Business", right: "Bereavement (Indicate Relationship)" },
      { left: "School Closed (as Indicated on Calendar)", right: "Emergency Closing (Snow Days, other authorized emergencies)" },
    ];

    timeNotAtWorkRows.forEach((row) => {
      worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = row.left;
      worksheet.getCell(`A${currentRow}`).border = thinBorder;

      worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
      worksheet.getCell(`C${currentRow}`).value = row.right;
      worksheet.getCell(`C${currentRow}`).border = thinBorder;

      currentRow++;
    });

    const codeData = [
      { cells: ["I", "J", "K", "L"], values: ["Code", "Explanation / Location", "Code", "Explanation / Location"] },
      { cells: ["I", "J", "K", "L"], values: ["1", "Assigned Role of Acting Director", "3", "Group Leader assigned to role of Small Group Leader"] },
      { cells: ["I", "J", "K", "L"], values: ["2", "Assigned to another program site Permanent sub assigned to a program outside of cluster", "4", "Group Leader assigned to role of Head of Group"] },
    ];

    const codeStartRow = currentRow - 3;
    codeData.forEach((row, rowIndex) => {
      row.cells.forEach((cell, cellIndex) => {
        worksheet.getCell(`${cell}${codeStartRow + rowIndex}`).value = row.values[cellIndex];
        worksheet.getCell(`${cell}${codeStartRow + rowIndex}`).border = thinBorder;
      });
    });

    currentRow += 1;

    worksheet.getRow(currentRow).values = [
      "Total Absences:",
      "______________________",
      "Total Lateness:",
      "______________________",
      "Total Left Early:",
      "______________________",
    ];
    currentRow += 2;

    worksheet.mergeCells(`A${currentRow}:N${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value =
      "I certify that the above account is true and correct and that the services performed were rendered to SCOPE on the dates and hours stated.";
    worksheet.getCell(`A${currentRow}`).alignment = { wrapText: true };
    currentRow += 2;

    [
      ["Employee Signature:", "______________________", "Date:", "______________________"],
      ["Verification Signature:", "______________________", "Date:", "______________________"],
    ].forEach((row) => {
      worksheet.getRow(currentRow).values = row;
      currentRow++;
    });

    worksheet.mergeCells(`I${currentRow - 2}:J${currentRow + 1}`);
    worksheet.getCell(`I${currentRow - 2}`).value = "Scope Office Use Only";
    worksheet.getCell(`I${currentRow - 2}`).border = thinBorder;

    ["Total Hours Worked:", "Additional Pay Due:", "Approved by:", "Notes:"].forEach((label, i) => {
      worksheet.mergeCells(`K${currentRow - 2 + i}:L${currentRow - 2 + i}`);
      worksheet.getCell(`K${currentRow - 2 + i}`).value = label;
      worksheet.getCell(`K${currentRow - 2 + i}`).border = thinBorder;
    });

    currentRow += 5;
  });

  worksheet.columns = [
    { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 },
    { width: 15 }, { width: 15 }, { width: 15 }, { width: 15 },
    { width: 15 }, { width: 15 }, { width: 15 }, { width: 20 },
    { width: 15 }, { width: 15 },
  ];

  const buffer = await workbook.xlsx.writeBuffer();
  return buffer;
};

export default ScopeEmployeeTimesheetExcel;
